// =====================================================================
// Liquid Memory, pricing math (single source of truth).
// ---------------------------------------------------------------------
// Pure JavaScript. NO Vite, NO React, NO env reads, so this same file
// imports cleanly from both:
//   • the browser (src/pages/portal/Billing.jsx, src/pages/Pricing.jsx)
//   • the Vercel serverless function (api/polar-savings-event.js)
//
// We charge a percentage of measured inference SAVINGS, not raw spend.
// The rate steps DOWN as the customer's monthly savings step UP, the
// same mechanic as marginal income-tax brackets, so there are no
// cliffs and no incentive to game the threshold.
//
//   Tier 1: $0 to $500     ->  20%  ("low friction, easy yes")
//   Tier 2: $500 to $5,000 ->  18%  ("growth-stage customers")
//   Tier 3: $5,000+         ->  15%  ("enterprise, lock them in")
//
// Worked example, savings = $5,000:
//     First  $500  × 20%  = $100.00
//     Next   $4,500 × 18% = $810.00
//                            -------
//     Total commission    = $910.00
//     Effective rate      =  18.2%
//
// All amounts are kept in INTEGER CENTS to avoid floating-point drift.
// Commission rounds to the nearest cent at the end (bankers/half-up).
// =====================================================================

export const TIER_BREAK_LOW_CENTS  =  50_000;   // $500
export const TIER_BREAK_HIGH_CENTS = 500_000;   // $5,000

export const PRICING_TIERS = Object.freeze([
  {
    id: "starter",
    /* Label rewrite 2026-05.
       Old: "$0 to $500 saved / mo" wrapped mid-phrase on the middle
       card ("$500 to $5,000 SAVED" then "/ MO" orphaned on the next
       line at the default landing-card width). Dropping "saved" is
       safe because the section heading ("The tiers"), the lede
       ("the cloud-LLM spend we provably remove"), and the
       paragraph above all establish that the numbers are
       savings-per-month. The shorter labels now fit on one
       monospaced line at every card width down to 280px. */
    label: "$0 to $500 / mo",
    rate: 0.20,
    rate_pct_label: "20%",
    floor_cents: 0,
    ceiling_cents: TIER_BREAK_LOW_CENTS,
    rationale: "For teams just trying us out. Low rate, no commitment, easy to leave if it doesn't work.",
  },
  {
    id: "growth",
    label: "$500 to $5,000 / mo",
    rate: 0.18,
    rate_pct_label: "18%",
    floor_cents: TIER_BREAK_LOW_CENTS,
    ceiling_cents: TIER_BREAK_HIGH_CENTS,
    rationale: "For teams in steady production. Real workloads, real savings, lower rate so you keep more of it.",
  },
  {
    id: "enterprise",
    label: "$5,000+ / mo",
    rate: 0.15,
    rate_pct_label: "15%",
    floor_cents: TIER_BREAK_HIGH_CENTS,
    ceiling_cents: Infinity,
    rationale: "For enterprise workloads. Above $5,000 saved per month the rate is capped at 15%.",
  },
]);

// Cents per unit on the Polar meter. We send `commission_cents` as the
// number of meter units; Polar charges $0.01 per unit. Keep this 1:1
// with the Polar product price to avoid double-conversion bugs.
export const POLAR_METER_CENTS_PER_UNIT = 1;

// ---------------------------------------------------------------------
// Per-customer pricing overrides
// ---------------------------------------------------------------------
//
// The `profiles.pricing_override` JSONB column lets an admin negotiate
// a different rate schedule for a single customer (e.g. a lower tier 3
// rate for a large committed customer).
//
// Override JSON shape, every field is optional. Missing fields fall
// back to the default tier schedule defined above.
//
//   {
//     "tier1_rate": 0.18,    // override the starter rate
//     "tier2_rate": 0.16,    // override the growth rate
//     "tier3_rate": 0.14,    // override the enterprise rate
//     "tier1_ceiling_cents": 50000,    // override the starter ceiling ($)
//     "tier2_ceiling_cents": 500000,   // override the growth ceiling ($)
//     "reason": "Negotiated rate, Q1 2026, $5k+ committed",
//     "set_by": "<admin user id>",
//     "set_at": "2026-01-15T18:00:00Z"
//   }
//
// We never let an override INCREASE a rate above the public schedule
// (defence against an admin fat-fingering 0.8 instead of 0.08). The
// `clampOverride` helper enforces that.

export function clampOverride(override) {
  if (!override || typeof override !== "object") return null;
  const out = { ...override };
  // Rates can only go DOWN. The public schedule is the ceiling.
  if (typeof out.tier1_rate === "number")
    out.tier1_rate = Math.max(0, Math.min(PRICING_TIERS[0].rate, out.tier1_rate));
  if (typeof out.tier2_rate === "number")
    out.tier2_rate = Math.max(0, Math.min(PRICING_TIERS[1].rate, out.tier2_rate));
  if (typeof out.tier3_rate === "number")
    out.tier3_rate = Math.max(0, Math.min(PRICING_TIERS[2].rate, out.tier3_rate));
  return out;
}

/**
 * Resolve the effective tier schedule for a customer with an optional
 * override. Returns a frozen array shaped like PRICING_TIERS.
 */
export function resolveTiers(override) {
  const safe = clampOverride(override);
  if (!safe) return PRICING_TIERS;
  const t1Ceil =
    typeof safe.tier1_ceiling_cents === "number"
      ? safe.tier1_ceiling_cents
      : PRICING_TIERS[0].ceiling_cents;
  const t2Ceil =
    typeof safe.tier2_ceiling_cents === "number"
      ? safe.tier2_ceiling_cents
      : PRICING_TIERS[1].ceiling_cents;
  const t1Rate =
    typeof safe.tier1_rate === "number"
      ? safe.tier1_rate
      : PRICING_TIERS[0].rate;
  const t2Rate =
    typeof safe.tier2_rate === "number"
      ? safe.tier2_rate
      : PRICING_TIERS[1].rate;
  const t3Rate =
    typeof safe.tier3_rate === "number"
      ? safe.tier3_rate
      : PRICING_TIERS[2].rate;
  return Object.freeze([
    { ...PRICING_TIERS[0], rate: t1Rate, rate_pct_label: fmtPctFlex(t1Rate), ceiling_cents: t1Ceil },
    { ...PRICING_TIERS[1], rate: t2Rate, rate_pct_label: fmtPctFlex(t2Rate), floor_cents: t1Ceil, ceiling_cents: t2Ceil },
    { ...PRICING_TIERS[2], rate: t3Rate, rate_pct_label: fmtPctFlex(t3Rate), floor_cents: t2Ceil },
  ]);
}

// Pct label that omits the decimal when the number is an integer
// percent (so "10%" not "10.0%", but "9.5%" stays "9.5%").
function fmtPctFlex(rate) {
  const pct = rate * 100;
  return (Number.isInteger(pct) ? pct.toString() : pct.toFixed(1)) + "%";
}

// ---------------------------------------------------------------------
// Free pilot / private-beta default
// ---------------------------------------------------------------------
// During the private beta, EVERYONE is free by default. No account is billed
// unless you explicitly mark it billable. Flip FREE_BETA to false to end the
// beta, after which the default reverts to paid (the tier schedule) and only
// accounts explicitly comped with { free_pilot: true } stay free.
//
// Per-account markers on profiles.pricing_override always win over the default:
//   { free_pilot: true }  -> always free (even after the beta ends)
//   { billable: true }    -> a paying account (charged even during the beta)
export const FREE_BETA = true;

export const FREE_PILOT_OVERRIDE = Object.freeze({ free_pilot: true, reason: "free pilot" });
// Set this on an account to opt it INTO billing during the free beta.
export const BILLABLE_OVERRIDE = Object.freeze({ billable: true, reason: "paying customer" });

export function isFreePilot(override) {
  if (override && typeof override === "object") {
    if (override.free_pilot === true) return true; // explicitly comped, always free
    if (override.billable === true) return false; // explicitly a paying account
  }
  // No explicit marker: free while the private beta is on, paid once it ends.
  return FREE_BETA;
}

// ---------------------------------------------------------------------
// Commission math
// ---------------------------------------------------------------------

/**
 * Compute commission owed (in cents) from raw savings (in cents) using
 * marginal/bracket pricing. Accepts an optional per-customer override
 * (see resolveTiers).
 *
 * @param {number} savingsCents - Non-negative integer cents.
 * @param {object} [override]   - Optional pricing_override JSON.
 * @returns {number} Commission owed in cents (rounded to the nearest).
 */
export function commissionForSavingsCents(savingsCents, override = null) {
  // Free pilot: a customer whose pricing_override carries `free_pilot: true`
  // is never billed, regardless of savings. Set the flag once on the profile
  // and it holds even after global billing is switched on. This is the single
  // source of truth, so the server biller AND the UI breakdown both honor it.
  if (isFreePilot(override)) return 0;
  const tiers = override ? resolveTiers(override) : PRICING_TIERS;
  const s = Math.max(0, Math.floor(Number(savingsCents) || 0));
  let owedExact = 0;
  let lastCap = 0;
  let remaining = s;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierWidth = tier.ceiling_cents - lastCap;
    const taken = Math.min(remaining, tierWidth);
    owedExact += taken * tier.rate;
    remaining -= taken;
    lastCap = tier.ceiling_cents;
  }

  // Round half away from zero to the nearest cent.
  return Math.round(owedExact);
}

/**
 * Return a per-tier breakdown so a UI (or invoice generator) can show
 * the customer exactly how their bill was computed. Honors override.
 *
 * Each entry is { tier, taken_cents, owed_cents }.
 * The sum of owed_cents == commissionForSavingsCents(savingsCents, override).
 */
export function commissionBreakdown(savingsCents, override = null) {
  const tiers = override ? resolveTiers(override) : PRICING_TIERS;
  const freePilot = isFreePilot(override);
  const s = Math.max(0, Math.floor(Number(savingsCents) || 0));
  let remaining = s;
  let lastCap = 0;
  const rows = [];
  for (const tier of tiers) {
    const tierWidth = tier.ceiling_cents - lastCap;
    const taken = Math.min(Math.max(remaining, 0), tierWidth);
    const owed = freePilot ? 0 : Math.round(taken * tier.rate);
    rows.push({ tier, taken_cents: taken, owed_cents: owed });
    remaining -= taken;
    lastCap = tier.ceiling_cents;
  }
  return rows;
}

/**
 * Return the marginal tier the customer's NEXT dollar of savings would
 * fall into. Useful for highlighting "you're currently in this tier"
 * in the UI. Honors override.
 */
export function currentTierFor(savingsCents, override = null) {
  const tiers = override ? resolveTiers(override) : PRICING_TIERS;
  const s = Math.max(0, Number(savingsCents) || 0);
  for (const tier of tiers) {
    if (s < tier.ceiling_cents) return tier;
  }
  return tiers[tiers.length - 1];
}

/**
 * Effective (blended) rate = commission_owed / savings.
 * Returns 0 when savings is 0. Otherwise a unitless ratio (0.20 = 20%).
 * Honors override.
 */
export function effectiveRateFor(savingsCents, override = null) {
  const s = Math.max(0, Math.floor(Number(savingsCents) || 0));
  if (s === 0) return 0;
  return commissionForSavingsCents(s, override) / s;
}

// ---------------------------------------------------------------------
// Shared formatters
// ---------------------------------------------------------------------

export function fmtUSDFromCents(cents) {
  if (cents == null || Number.isNaN(cents)) return "";
  return (
    "$" +
    (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export function fmtUSDFromCentsCompact(cents) {
  if (cents == null || Number.isNaN(cents)) return "";
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1e6)
    return "$" + (dollars / 1e6).toFixed(2) + "M";
  if (Math.abs(dollars) >= 1e3)
    return "$" + (dollars / 1e3).toFixed(1) + "K";
  return (
    "$" +
    dollars.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
}

export function fmtPct(ratio, digits = 1) {
  if (ratio == null || Number.isNaN(ratio)) return "";
  return (ratio * 100).toFixed(digits) + "%";
}
